/**
 * User Management API Types
 * Following the API specification for User Management MicroService
 */

// StatusResponse - Base response structure
export interface StatusResponse {
  status: "SUCCESS" | "FAILURE";
  statusinfo: {
    code: string;
    message: string;
  };
  errors?: Record<string, string> | object | null;
}

// ValidateTokenRequest
export interface ValidateTokenRequest {
  token: string;
}

// ValidateTokenResponseData - User details from validated token
export interface ValidateTokenResponseData {
  firstName: string;
  lastName: string;
  email: string;
  roleName: string;
  // Account Setup specific fields (optional, may be present for account setup tokens)
  accountName?: string;
  accountType?: string;
  country?: string;
  phoneType?: string;
  phoneNumber?: string;
}

// ValidateTokenSuccessResponse
export interface ValidateTokenSuccessResponse extends StatusResponse {
  status: "SUCCESS";
  data: ValidateTokenResponseData;
  errors: null;
}

// ValidateTokenErrorResponse
export interface ValidateTokenErrorResponse extends StatusResponse {
  status: "FAILURE";
  data: Record<string, never>;
  errors: {
    [key: string]: string;
  };
}

export type ValidateTokenResponse =
  | ValidateTokenSuccessResponse
  | ValidateTokenErrorResponse;

/**
 * Type guard to check if validate token response is successful
 */
export function isValidateTokenSuccess(
  response: ValidateTokenResponse
): response is ValidateTokenSuccessResponse {
  return response.status === "SUCCESS";
}

// UserActivationRequest
export interface UserActivationRequest {
  userId?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phoneType?: string;
  phoneNumber?: string;
  password: string;
  confirmPassword: string;
}

// UserActivationResponse
export interface UserActivationResponse {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
}

// UserActivationSuccessResponse
export interface UserActivationSuccessResponse extends StatusResponse {
  status: "SUCCESS";
  data: UserActivationResponse;
  errors: null;
}

export interface UserActivationErrorResponse extends StatusResponse {
  status: "FAILURE";
  data: Record<string, never>;
  errors: {
    [key: string]: string;
  };
}

export type UserActivationAPIResponse =
  | UserActivationSuccessResponse
  | UserActivationErrorResponse;

/**
 * Type guard to check if user activation response is successful
 */
export function isUserActivationSuccess(
  response: UserActivationAPIResponse
): response is UserActivationSuccessResponse {
  return response.status === "SUCCESS";
}

// ContactNumberItem - Contact number entry with phone type and number
export interface ContactNumberItem {
  phoneType: string;
  phoneNumber: string;
}

// WorkInfoItem - Work information entry
export interface WorkInfoItem {
  jobTitle?: string;
  department?: string;
  supervisor?: string;
}

/** Tenant / account metadata returned with userDetails (shared via localStorage) */
export interface AccountDetails {
  accountId: number;
  accountName: string;
  accountCode: string;
}

// UserDetailsResponse - Response from /userDetails endpoint (GET)
export interface UserDetailsResponse {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  roles?: string[];
  lastLogin?: string;
  createdDate?: string;
  contactNumber?: ContactNumberItem[];
  timeZone?: string;
  workInfo?: WorkInfoItem;
  /** Tenant account details — persisted in linn-i-am-userDetails for all micro-apps */
  accountDetails?: AccountDetails;
}

// UserDetailsUpdateResponse - Response from /userProfile endpoint (POST update)
// Fields that weren't updated will be null
export interface UserDetailsUpdateResponse {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  status: string | null;
  roles?: string[];
  lastLogin?: string;
  createdDate?: string;
  contactNumber?: ContactNumberItem[] | null;
  timeZone?: string | null;
  workInfo?: WorkInfoItem | null;
}

// UserDetailsSuccessResponse - For GET /userDetails
export interface UserDetailsSuccessResponse extends StatusResponse {
  status: "SUCCESS";
  data: {
    usersDetails: UserDetailsResponse[];
    /** Present when API returns accountDetails at the data level (mirrored onto usersDetails[0] on store) */
    accountDetails?: AccountDetails;
  };
  errors: null;
}

// UserDetailsUpdateSuccessResponse - For POST /userProfile (update)
export interface UserDetailsUpdateSuccessResponse extends StatusResponse {
  status: "SUCCESS";
  data: {
    usersDetails: UserDetailsUpdateResponse[];
  };
  errors: null;
}

// UserDetailsErrorResponse
export interface UserDetailsErrorResponse extends StatusResponse {
  status: "FAILURE";
  data: Record<string, never>;
  errors: {
    [key: string]: string;
  };
}

export type UserDetailsAPIResponse =
  | UserDetailsSuccessResponse
  | UserDetailsErrorResponse;

export type UserDetailsUpdateAPIResponse =
  | UserDetailsUpdateSuccessResponse
  | UserDetailsErrorResponse;

/**
 * Type guard to check if user details response is successful (GET)
 */
export function isUserDetailsSuccess(
  response: UserDetailsAPIResponse
): response is UserDetailsSuccessResponse {
  return response.status === "SUCCESS";
}

/**
 * Type guard to check if user details update response is successful (POST)
 */
export function isUserDetailsUpdateSuccess(
  response: UserDetailsUpdateAPIResponse
): response is UserDetailsUpdateSuccessResponse {
  return response.status === "SUCCESS";
}

